/**
 * The offer the customer sees before the menu editor — `build/FIXES.md` item
 * 76 (F5), ruling 7 of 8 October 2026, which the amendment of that day
 * records as superseding two lines of §1 of `docs/overnight_constraints.md`:
 * *no step-by-step flow* and *keep the quiet sticky order summary*.
 *
 * The card **replaces** the §5.4 side summary rather than sitting beside it,
 * so everything that summary showed is here, row for row, under the same
 * `.summary` and `#sumBody` the stylesheet and the suites already know. What
 * is added above those rows is the offer itself: the dishes grouped by the
 * four §5.11 price categories, each with its photograph where one exists, its
 * name and the quantity 04 Összesítés prints for it — the same quantity
 * information, no more and no less, and **no price per dish** (§1 of the
 * constraints, which this ruling leaves standing).
 *
 * Nothing on this card is a new control (ruling 8). The two buttons under the
 * totals are the two item 76 names, and the order button stays where it is,
 * at the foot of 04, with its label F8's to settle in `083_confirm_first`.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { OrderLine } from '@plt/engine'
import { budapestDate, eventTimeLabel, toNumber } from '@plt/engine'

import type { PageBundle } from '../bundle.ts'
import { PRICE_CATEGORY_LABELS } from '../catalogue.ts'
import { DishName, LookalikeFootnote, PlantBasedTag } from '../components.tsx'
import {
  COFFEE_MACHINE_LINE,
  fulfilmentDate,
  fulfilmentWindow,
  isReception,
  lookalikeIn,
  nameMarksIn,
  offerIsReady,
} from '../model.ts'
import { continueToOrder } from '../missing.ts'
import { MenuCheckLines, MinimumOrderLine, PickupCallout, mealNote } from './Notices.tsx'
import { unitTotals } from './Step4Summary.tsx'

/* ------------------------------------------------------------------ *
 * Moving about the page, and the two seconds of highlight
 * ------------------------------------------------------------------ */

/** Item 76: *Both respect reduced-motion settings.* */
const wantsNoMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * `scroll-behavior:smooth` is already behind `prefers-reduced-motion:
 * no-preference` in the stylesheet, but an explicit `behavior` on
 * `scrollIntoView` overrides the stylesheet, so a viewer who asked for no
 * motion would get it back. The behaviour is therefore read from the same
 * query rather than written down.
 */
const scrollToElement = (element: Element): void => {
  element.scrollIntoView({ behavior: wantsNoMotion() ? 'auto' : 'smooth', block: 'start' })
}

/** Item 76: *highlights it for two seconds.* */
const HIGHLIGHT_MS = 2000

/**
 * Where a dish sits in the menu editor. A savoury or sweet dish is a `.pick`
 * row — `data-gfauto` where Planteen added it for the gluten-free guests —
 * fruit is `data-fruit`, and a drink is the `.xrow` around its tick box.
 */
function editorRowFor(dishId: string): HTMLElement | null {
  const key = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(dishId) : dishId
  const row = document.querySelector<HTMLElement>(
    `[data-pick="${key}"],[data-gfauto="${key}"],[data-fruit="${key}"]`,
  )
  if (row !== null) return row
  const drink = document.querySelector<HTMLElement>(`input[data-x="${key}"]`)
  return drink === null ? null : drink.closest<HTMLElement>('.xrow')
}

/* ------------------------------------------------------------------ *
 * The card
 * ------------------------------------------------------------------ */

export function OfferCard({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, calendar } = page
  const ready = offerIsReady(state, calendar)

  /* The row a tap landed on, so the class can come off again. Held in a ref
     rather than in page state: the highlight is a two-second mark on one DOM
     node and nothing the page computes depends on it. */
  const lit = useRef<{ row: HTMLElement; timer: number } | null>(null)
  const clearHighlight = useCallback(() => {
    if (lit.current === null) return
    window.clearTimeout(lit.current.timer)
    lit.current.row.classList.remove('offerhit')
    lit.current = null
  }, [])
  useEffect(() => clearHighlight, [clearHighlight])

  const reveal = useCallback(
    (dishId: string) => {
      const row = editorRowFor(dishId)
      if (row === null) return
      scrollToElement(row)
      clearHighlight()
      row.classList.add('offerhit')
      lit.current = { row, timer: window.setTimeout(() => clearHighlight(), HIGHLIGHT_MS) }
    },
    [clearHighlight],
  )

  /* A dish the filters have taken off the screen cannot be scrolled to, so
     the tap puts Mind back first and the scroll waits for that render. Every
     dish on the card is a dish on the menu, so Mind always shows it. */
  const [pending, setPending] = useState<string | null>(null)
  useEffect(() => {
    if (pending === null) return
    setPending(null)
    reveal(pending)
  }, [pending, reveal])

  /* Fix list item 9: on one column the panel is a sheet over the page, so
     every jump out of it closes it first. */
  const tapDish = useCallback(
    (dishId: string) => {
      if (page.locked) return
      set({ offerOpen: false })
      if (editorRowFor(dishId) === null) {
        set({ filter: 'all', sub: null })
        setPending(dishId)
        return
      }
      reveal(dishId)
    },
    [page.locked, set, reveal],
  )

  const toTopOfEditor = useCallback(() => {
    clearHighlight()
    set({ offerOpen: false })
    const editor = document.getElementById('sec2')
    if (editor !== null) scrollToElement(editor)
  }, [clearHighlight, set])

  /* Run 10 item 5: *Tovább a rendeléshez* goes straight to the contact step
     and puts the cursor in its first name field — the family name in
     Hungarian, the given name in English, because the two change places with
     the language (§5.12). The sheet closes first on one column. */
  /* Specification §3.1 (DECIDED 20261023): while something is missing, the
     button goes to the first gap on the page instead — scrolled to, focused,
     marked invalid and highlighted — rather than leaving the customer to
     read the list. */
  const { goToFirstMissing } = page
  const toCheckout = useCallback(() => {
    set({ offerOpen: false })
    continueToOrder(goToFirstMissing, () => focusFirstNameField(t.lang))
  }, [set, t.lang, goToFirstMissing])

  return (
    <aside
      className={state.offerOpen ? 'summary open' : 'summary'}
      id="offer"
      data-testid="offer-card"
    >
      <div className="card tight">
        <SummaryHead page={page} />
        {!ready ? (
          <p id="offerEmpty" style={{ margin: 0 }}>
            {t(
              'Adja meg a rendezvény adatait, és itt látja az ajánlatunkat.',
              'Enter your event details and your offer appears here.',
            )}
          </p>
        ) : (
          <>
            <h3 id="offerHd">
              {t(
                'Az Ön rendezvényére a következő menüt ajánljuk:',
                'For your event, we recommend the following menu:',
              )}
            </h3>
            {/* Run 10 item 2: one muted line directly under the heading. Every
                dish on the card is a button that scrolls to its row in the
                editor (`tapDish`), savoury, sweet, fruit and drinks alike. */}
            <p className="sub offerhelp" id="offerHelp">
              {t(
                'A termékekre kattintva módosíthatja a mennyiségeket, és másik terméket is választhat.',
                'Tap any item to change its quantity or swap it for another.',
              )}
            </p>
            {/* Run 10 item 7a, the third of the three places. */}
            <div className="pbtagrow">
              <PlantBasedTag page={page} id="pbOffer" />
            </div>
            {/* Run 10 item 4: one table for the dishes and the delivery, so
                the quantity and amount columns line up from top to bottom. */}
            <div id="sumBody">
              <div className="offertable">
                <OfferDishes page={page} onTap={tapDish} />
                <OfferCoffee page={page} />
                <OfferFees page={page} />
              </div>
              <OfferTotals page={page} />
            </div>
            <LookalikeFootnote page={page} id="offerLookalikeNote" show={offerHasLookalike(page)} />
            <div className="offeracts" id="offerActs">
              <button type="button" id="offerEdit" className="offerbtn" onClick={toTopOfEditor}>
                {t('Ajánlat módosítása', 'Change the offer')}
              </button>
              {/* Item 76 names *the existing continue button* here. The order
                  button is `#go`, at the foot of 04, and §1 of the constraints
                  still asks for a strong, explicit final checkout — so it stays
                  there and this takes the customer to it. Its label is a
                  default chosen in run7; F8 settles `#go`'s own. */}
              <button type="button" id="offerGo" className="cta" onClick={toCheckout}>
                {t('Tovább a rendeléshez', 'Continue to your order')}
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  )
}

/* ------------------------------------------------------------------ *
 * The dishes
 * ------------------------------------------------------------------ */

/**
 * The same lines 04 Összesítés prints, in the same four groups, with the same
 * quantity and unit — and nothing else. No price per dish, no production
 * unit, no rounding, no minimum batch (§1 and §3 of the constraints).
 *
 * Item 76 names savoury, sweet and drinks, which is the three the catalogue
 * publishes into today; fruit is listed under its own §5.11 heading rather
 * than dropped, so the day a *Gyümölcs* row is published nothing is lost.
 */
function OfferDishes(props: { page: PageBundle; onTap: (dishId: string) => void }): JSX.Element {
  const { page, onTap } = props
  const { t, f } = page
  const business = page.state.business
  const groups = page.priced.priced.categories.filter((category) => category.lines.length > 0)
  return (
    <div id="offerDishes">
      {/* No group means no line to print. The card says nothing rather than
          saying so in a sentence the brief did not give and Anikó has not
          read. */}
      {groups.map((category) => (
          <div className="offergrp" key={category.category} data-offergrp={category.category}>
            {/* Fix list item 9: with 04's summary card gone, the price per
                category (§5.11) is printed here — per category, never per
                dish. Net for a business buyer, as every other figure. */}
            {/* Run 10 item 4: the group's own header row, then its dishes —
                name, quantity, and an amount column that stays empty on a dish
                line (no price per dish, §1 of the constraints) — then its
                subtotal on a row of its own. */}
            <h4 className="offergrphd">
              <span>{t.of(PRICE_CATEGORY_LABELS[category.category] ?? { hu: category.category, en: category.category })}</span>
            </h4>
            {category.lines.map((line) => (
              <OfferDish
                key={`${line.dishId}${line.isGlutenFreePortion ? '-gf' : ''}`}
                page={page}
                line={line}
                onTap={onTap}
              />
            ))}
            <div className="offersub" data-offersub={category.category}>
              <span className="k">
                {business ? t('Részösszeg, nettó', 'Subtotal, net') : t('Részösszeg', 'Subtotal')}
              </span>
              <span className="num offersubqty">
                {unitTotals(category.lines)
                  .map(([unit, count]) => `${f.n(count)} ${unitWord(page, unit, count)}`)
                  .join(' + ')}
              </span>
              <span className="num offergrpamt">{f.money(business ? category.net : category.gross)}</span>
            </div>
          </div>
      ))}
    </div>
  )
}

/**
 * §5.11's own unit words, because the quantity on the card is the quantity on
 * the summary: *db* or *adag*, and the English has to agree with the count.
 */
const unitWord = (page: PageBundle, unit: string, count: number): string =>
  unit === 'adag'
    ? page.t('adag', `portion${page.f.plural(count, '', 's')}`)
    : page.t('db', 'pcs')

function OfferDish(props: {
  page: PageBundle
  line: OrderLine
  onTap: (dishId: string) => void
}): JSX.Element {
  const { page, line, onTap } = props
  const { t, f } = page
  const dish = page.priced.byId.get(line.dishId) ?? page.dishes.find((d) => d.id === line.dishId)
  const label = dish === undefined ? line.dishId : t(dish.nameHu, dish.nameEn)
  // DECIDED 20261101: the publication's photo, the same data as the menu's.
  const photo = dish?.photo ?? null
  return (
    <button
      type="button"
      className="offerdish"
      data-offer={line.dishId}
      onClick={() => onTap(line.dishId)}
    >
      <span className="offername">
        {photo === null ? null : (
          <img
            className="offerthumb"
            src={photo.thumb}
            srcSet={`${photo.thumb} 400w, ${photo.card} 800w`}
            sizes="64px"
            width={400}
            height={400}
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
        <span className="nm">
          <DishName label={label} marks={nameMarksIn(dish, page.t.lang)} />
          {/* Run 10 item 3: the gluten-free box is named in a small, muted
              inline tag — no filled badge, no icon. */}
          {line.isGlutenFreePortion ? (
            <>
              {' '}
              <span className="offergf" data-gfline="">
                {t('gluténmentes, külön doboz', 'gluten-free, boxed separately')}
              </span>
            </>
          ) : line.glutenFreePortions > 0 ? (
            <>
              {' '}
              {/* Run 17 item B5(b): the dish is on the menu and carries the
                  gluten-free guests' share inside its own quantity, so the
                  share is said here rather than drawn as a second line. The
                  kitchen still packs it in its own labelled box. */}
              <span className="offergf" data-gfshare={line.dishId}>
                {t(
                  `ebből ${f.n(line.glutenFreePortions)} db gluténmentes vendégeknek`,
                  `of which ${f.n(line.glutenFreePortions)} for gluten-free guests`,
                )}
              </span>
            </>
          ) : null}
        </span>
      </span>
      <span className="num offerqty">
        {f.n(line.orderedPortions)} {unitWord(page, line.unit, line.orderedPortions)}
      </span>
      <span className="offeramt" aria-hidden="true" />
    </button>
  )
}

/* ------------------------------------------------------------------ *
 * §5.4, the quiet sticky summary — every row of it, in the card
 * ------------------------------------------------------------------ */

/**
 * **Run 19 §16: the coffee, as two offer lines.**
 *
 *   *Kávé (eszpresszó és tejes kávék)* / *Coffee (espresso and milk drinks)*
 *   — in cups, which replaces the Americano drinks line the old design drew;
 *   *Kávégép, {n} db* / *Coffee machine, {n}* — the machines for the day.
 *
 * Two lines, because they move on different things: one more guest adds
 * cups, the fifty-first guest adds a whole machine. They are drawn only
 * when the customer actually asked for coffee, which is what fills
 * `totals.total.services`; the question being *there* charges nothing.
 *
 * §5.11: the amounts are gross for a consumer and net for a business buyer,
 * like every other amount those two read. Nothing here shows a cost, a
 * markup or a margin.
 */
function OfferCoffee({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, f, state, totals } = page
  const services = totals.total.services
  if (services.length === 0) return null
  const business = state.business
  const label = (key: string, quantity: number): string =>
    key === COFFEE_MACHINE_LINE
      ? t(`Kávégép, ${f.n(quantity)} db`, `Coffee machine, ${f.n(quantity)}`)
      : t('Kávé (eszpresszó és tejes kávék)', 'Coffee (espresso and milk drinks)')
  const quantityOf = (key: string, quantity: number): string =>
    key === COFFEE_MACHINE_LINE ? '' : t(`${f.n(quantity)} csésze`, `${f.n(quantity)} cups`)
  return (
    <div className="offergrp offercoffee" data-offergrp="coffee">
      <h4 className="offergrphd">
        <span>{t('Kávé', 'Coffee')}</span>
      </h4>
      {services.map((line) => (
        <div
          key={line.key}
          className="offersub sumrow"
          data-offersub={line.key}
          data-coffeeline={line.key}
        >
          <span className="k">{label(line.key, line.quantity)}</span>
          <span className="num offersubqty">{quantityOf(line.key, line.quantity)}</span>
          <span className="num offergrpamt">{f.money(business ? line.net : line.gross)}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Run 10 item 4: delivery is a group of its own, after the dishes, with its
 * own header row and the same three columns as the dish groups.
 */
function OfferFees({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, totals, slots, calendar, param } = page
  const business = state.business
  const delivery = totals.total.delivery

  /* Run 16 item 2 (DECIDED 20261015, Barna): the delivery or collection time
     the customer reads here carries its date and its weekday, through the one
     shared formatter. The label above it — *Szállítás* / *Átvétel* — is the
     one that was already here. Nothing is shown while the date or the window
     is not settled, or while delivery is not to be had (item 32). */
  const whenLine = delivery.blocked
    ? ''
    : eventTimeLabel(
        f.lang,
        (() => {
          const day = fulfilmentDate(state, calendar)
          return day === null ? '' : budapestDate(day)
        })(),
        fulfilmentWindow(state, slots, calendar, param),
      )

  /* build/FIXES.md item 32: the label beside it already reads Szállítás, so
     the value says only that it is not to be had, and carries no amount. */
  const feeLabel = delivery.blocked
    ? t('nem elérhető', 'not available')
    : delivery.pickup
      ? f.money(0)
      : delivery.quoted
        ? t('egyedi', 'quoted')
        : delivery.free
          ? t('ingyenes', 'free')
          : f.money(business ? delivery.feeNet : delivery.feeGross)

  const freeHint =
    delivery.blocked || totals.view.remainingToFreeShown === null || !page.flag('free_delivery_hint')
      ? null
      : t(
          `Ha még ${business ? 'nettó ' : ''}${f.money(totals.view.remainingToFreeShown)} értékben rendel, a szállítás ingyenes.`,
          `Order ${business ? 'net ' : ''}${f.money(totals.view.remainingToFreeShown)} more and delivery is free.`,
        )

  return (
    <div className="offergrp offerfees" data-offergrp="fees">
      <h4 className="offergrphd">
        <span>{delivery.pickup ? t('Átvétel', 'Pickup') : t('Szállítás', 'Delivery')}</span>
      </h4>
      <div className="offersub offerfee sumrow" data-offersub="fees">
        <span className="k">
          {delivery.pickup
            ? t('Személyes átvétel', 'Pickup')
            : business && !delivery.blocked
              ? t('Szállítás, nettó', 'Delivery, net')
              : t('Szállítás', 'Delivery')}
        </span>
        <span className="num offersubqty" />
        <span className="num offergrpamt">{feeLabel}</span>
      </div>
      {whenLine === '' ? null : (
        <p className="sub offerspan" data-offerwhen={delivery.pickup ? 'pickup' : 'delivery'} style={{ margin: '0 0 4px' }}>
          {whenLine}
        </p>
      )}
      {freeHint === null ? null : (
        <p className="sub offerspan" style={{ margin: '0 0 4px' }}>
          {freeHint}
        </p>
      )}
    </div>
  )
}

/**
 * This is `shell.tsx`'s `StickySummary()` body, moved rather than rewritten:
 * guests, occasion, roughly how much food, the food and drinks line, the
 * delivery line, the free-delivery hint, the total, the per-guest figure, the
 * VAT or gross note and the enquiry or short-notice badge. Item 76 asks for
 * the gross total, the gross per head and the delivery fee where it applies,
 * and those are three of these rows; the rest are what *nothing is lost*
 * means.
 *
 * The two sides are printed to one decimal, which is what the balance hint in
 * step 01 prints — rounded to whole numbers they said 6 and 3 where step 01
 * said 5,9 and 2,9, the same quantity printed two ways on one screen. The
 * headline above them is the true total, stated flat, while the two sides are
 * prefixed *kb.* / *about*: rounding each side on its own can leave their sum
 * 0,1 from the headline, and that is what the *kb.* is for.
 */
function OfferTotals({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, priced, totals, rules, calendar, param } = page
  const business = state.business
  const guests = priced.guests
  const perGuest = toNumber(priced.priced.perGuest.total)
  const savoury = toNumber(priced.priced.perGuest.savoury)
  const sweet = toNumber(priced.priced.perGuest.sweet)
  const delivery = totals.total.delivery

  const row = (label: string, value: string, className = 'sumrow'): JSX.Element => (
    <div className={className}>
      <span className="k">{label}</span>
      <span className="num" style={{ textAlign: 'right' }}>
        {value}
      </span>
    </div>
  )

  return (
    <>
      {/* Run 10 item 4: the totals, set apart from the item list. */}
      <div className="offertotals" id="offerTotals">
        {row(
          business ? t('Étel és ital, nettó', 'Food and drinks, net') : t('Étel és ital', 'Food and drinks'),
          f.money(business ? priced.priced.food.net : priced.priced.food.gross),
        )}
        <div className="sumrow big">
          <span>{business ? t('Összesen, nettó', 'Total, net') : t('Összesen', 'Total')}</span>
          <span className="num">{f.money(business ? totals.total.net : totals.total.gross)}</span>
        </div>
        {/* Fix list item 9: with 04's card gone, a business buyer reads the
            VAT and the gross payable here (§19.8 no. 21). */}
        {business ? row(t(`ÁFA ${param('vat_rate')}%`, `VAT ${param('vat_rate')}%`), f.money(totals.total.vat)) : null}
        {business
          ? row(t('Bruttó összesen, fizetendő', 'Total, gross, payable'), f.money(totals.total.gross))
          : null}
        {row(
          business ? t('Vendégenként, nettó', 'Per guest, net') : t('Vendégenként', 'Per guest'),
          f.money(business ? totals.view.perGuest.net : totals.view.perGuest.gross),
          'sumrow perguest',
        )}
        {/* Run 17 item B7 (DECIDED 20261015, Barna): the minimum order value,
            said here rather than only at the delivery step. The existing
            delivery-step notice stays where it is. */}
        <MinimumOrderLine page={page} id="offerMinimum" />
        <p className="sub" style={{ margin: '6px 0 0' }}>
          {business ? '' : t('Bruttó árak.', 'Gross prices.')}
          {delivery.quoted
            ? t(' A szállítási díjat egyedileg adjuk meg.', ' The delivery fee is quoted individually.')
            : ''}
        </p>
      </div>

      <div className="offerfacts">
        {row(
          t('Vendég', 'Guests'),
          f.n(guests) +
            (priced.glutenFreeGuests > 0
              ? t(
                  `, ebből ${f.n(priced.glutenFreeGuests)} gm.`,
                  `, of whom ${f.n(priced.glutenFreeGuests)} gluten-free`,
                )
              : ''),
        )}
        {row(t('Alkalom', 'Occasion'), occasionName(page))}
        {/* Run 17 item B11(c), as run 19 §4 extends it: what the meal
            question was answered, in either direction. */}
        {mealNote(page) === null ? null : (
          <p className="sub" id="offerMealNote" data-mealnote="light" style={{ margin: '0 0 6px' }}>
            {mealNote(page)}
          </p>
        )}
        {/* Run 19 §8: a collection on a day other than the event date,
            where the offer card already carries the fulfilment time. */}
        <PickupCallout page={page} id="offerPickupAlert" />
        <div className="perhead">
          <div className="tot">
            <span className="v num">{f.n(perGuest, 1)}</span>
            <span className="l">{t('falat vendégenként', 'pieces per guest')}</span>
          </div>
          <div className="sub">
            {t(
              `kb. ${f.n(savoury, 1)} sós és ${f.n(sweet, 1)} édes`,
              `about ${f.n(savoury, 1)} savoury and ${f.n(sweet, 1)} sweet`,
            )}
          </div>
        </div>
      </div>
      {/* Moved from 04 with the summary card (fix list item 9). Run 10 item
          7a: the plant-based statement is the tag under the heading now, so
          this note keeps only the estimate. */}
      <p className="sub" id="estNote" style={{ margin: '6px 0 0' }}>
        {t(
          'A mennyiség szakmai becslés, a rendezvényeink alapján.',
          'The quantities are a professional estimate, based on our events.',
        )}
      </p>
      {rules.enquiry.length > 0 ? (
        <div className="note stop" style={{ marginBottom: 0 }}>
          {t('Ajánlatkérés', 'Enquiry')}
        </div>
      ) : calendar.ok && calendar.lane === 'short' ? (
        <div className="note" style={{ marginBottom: 0 }}>
          {t('Rövid határidő', 'Short notice')}
        </div>
      ) : null}
    </>
  )
}

/** Run 10 item 7b: the card's footnote shows only when one of its dishes is marked. */
const offerHasLookalike = (page: PageBundle): boolean =>
  page.priced.priced.categories.some((category) =>
    category.lines.some((line) => {
      const dish = page.priced.byId.get(line.dishId) ?? page.dishes.find((d) => d.id === line.dishId)
      return lookalikeIn(dish, page.t.lang)
    }),
  )

/**
 * Run 10 item 5: the first name field of 03 as the customer sees it — the
 * family name in Hungarian, the given name in English (§5.12) — scrolled to
 * and focused. The scroll is the page's own; the focus does not scroll again.
 */
function focusFirstNameField(lang: string): void {
  const field = document.getElementById(lang === 'hu' ? 'cLast' : 'cFirst') as HTMLInputElement | null
  const section = document.getElementById('sec3')
  if (section !== null) scrollToElement(section)
  if (field === null) return
  field.focus({ preventScroll: true })
}

/**
 * **The summary card's head (redesign, DECIDED 20261101).** Something from
 * the first frame, never an empty box: the label, the headcount and the date
 * the customer has entered so far, and the occasion they chose. Only values
 * step 01 already shows; no price until the offer card has one.
 */
function SummaryHead({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, calendar } = page
  const guests = Number(state.guestsField)
  const hasGuests = state.guestsField.trim() !== '' && Number.isFinite(guests) && guests >= 1
  const date = calendar.ok && calendar.eventDate !== null ? f.date(calendar.eventDate, false) : null
  return (
    <div className="sumhead" id="sumHead" data-testid="summary-head">
      <span className="sumlabel">{t('Az Ön ajánlata', 'Your offer')}</span>
      <div className="sumbig">
        <span className="num" id="sumGuests">{hasGuests ? f.n(guests) : '—'}</span>{' '}
        <small>
          {t('vendég', hasGuests && guests === 1 ? 'guest' : 'guests')}
          {date === null ? '' : ` · ${date}`}
        </small>
      </div>
      <div className="sumrow sumocc">
        <span className="k">{t('Alkalom', 'Occasion')}</span>
        <span>{occasionName(page)}</span>
      </div>
    </div>
  )
}

/**
 * Run 17 item B9(a): the two reception rows read as one occasion here too,
 * so the offer card names what the customer chose. Every other occasion
 * keeps the register's own name.
 */
const occasionName = (page: PageBundle): string => {
  if (isReception(page.state.occasion)) return page.t('Állófogadás', 'Standing reception')
  const row = page.context.occasions.find((candidate) => candidate.key === page.state.occasion)
  return row === undefined
    ? page.state.occasion
    : page.t(row.name_hu ?? row.key, row.name_en ?? row.name_hu ?? row.key)
}

/* ------------------------------------------------------------------ *
 * The slim bar, on one column only
 * ------------------------------------------------------------------ */

/**
 * Item 76: *A slim bar fixed to the bottom of the screen shows the gross
 * total and* Ajánlat*, which scrolls back to the card. The bar follows the
 * safe-area rules and hides while the card itself is on screen.*
 *
 * The gross total, for every buyer: a business buyer's card leads on the net
 * figure and states the gross under it, and the bar is the one line that has
 * to agree with what the order is worth — `totals.total.gross`, the figure
 * `submit_order` is given as `client_gross`.
 *
 * The bar is drawn only below the two-column breakpoint, by the stylesheet,
 * and the node is always rendered so the observer has something stable to
 * watch.
 */
export function OfferBar({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, set, totals, priced, calendar } = page
  const ready = offerIsReady(state, calendar)
  /* Run 17 item B4: *On mobile, show both lines in the fixed bottom bar as
     well while the dish list is on screen.* The bar itself is drawn by the
     stylesheet below the two-column breakpoint and nowhere else, so the one
     thing to work out here is whether the dish list is in view — watched
     rather than guessed from a scroll position. */
  const dishListShown = useElementOnScreen('dishList')
  /* The page reserves the bar's height at its foot so the last control of 04
     and the legal footer are never under it. Item B4 made the bar taller on
     one column, and a height nobody can write down is one to measure: the bar
     writes its own into `--offerbar-h` and the stylesheet reads it. */
  const barRef = useRef<HTMLDivElement | null>(null)
  /* Fix list item 9 (Barna, 10 October 2026): on one column the side panel
     is this bar — guests and the gross total, always there once there is an
     offer — and a tap expands it into the full panel, dish jumps included.
     It no longer hides while an in-flow card is on screen, because there is
     no in-flow card any more. */
  const hidden = !ready
  const guests = priced.guests
  useEffect(() => {
    const bar = barRef.current
    const root = document.documentElement
    if (bar === null) return
    const write = (): void => {
      const height = hidden ? 0 : Math.round(bar.getBoundingClientRect().height)
      root.style.setProperty('--offerbar-h', height + 'px')
    }
    write()
    if (typeof ResizeObserver !== 'function') return
    const observer = new ResizeObserver(write)
    observer.observe(bar)
    return () => observer.disconnect()
  })
  return (
    <div
      ref={barRef}
      className={hidden ? 'offerbar hide' : 'offerbar'}
      id="offerBar"
      aria-hidden={hidden}
    >
      <button
        type="button"
        className="offerbartoggle"
        id="offerBarGo"
        aria-expanded={state.offerOpen}
        aria-controls="offer"
        onClick={() => set({ offerOpen: !state.offerOpen })}
      >
        <span className="offerbarguests">
          {f.n(guests)} {t('vendég', `guest${f.plural(guests, '', 's')}`)}
        </span>
        <span className="num" id="offerBarTotal">
          {f.money(totals.total.gross)}
        </span>
        <span className="offerbarmore">
          {state.offerOpen ? t('Bezárás', 'Close') : t('Részletek', 'Details')}{' '}
          <span aria-hidden="true">{state.offerOpen ? '▾' : '▴'}</span>
        </span>
      </button>
      {/* Item B4's second place: the same two lines, from the same one
          value, so the bar and the menu cannot disagree. */}
      {dishListShown && !state.offerOpen ? (
        <MenuCheckLines page={page} id="offerBarCheck" />
      ) : null}
    </div>
  )
}

/**
 * Whether the element with this id is on screen. Item B4 asks for the check
 * lines in the bottom bar *while the dish list is on screen*, which is a
 * question about the viewport rather than about the page's state, so it is
 * observed and kept in a ref-free piece of local state.
 *
 * Where `IntersectionObserver` is not there — jsdom, an old browser — the
 * answer is **true**: showing the lines is the safe side of the choice, and
 * the bar is only drawn on one column in the first place.
 */
function useElementOnScreen(id: string): boolean {
  const [shown, setShown] = useState(true)
  useEffect(() => {
    const element = document.getElementById(id)
    if (element === null || typeof IntersectionObserver !== 'function') {
      setShown(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1]
        if (entry !== undefined) setShown(entry.isIntersecting)
      },
      { threshold: 0 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [id])
  return shown
}
